import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  WHATSAPP_DEFAULTS,
  WHATSAPP_EVENTS,
  type WhatsAppLanguage,
  type WhatsAppOverviewDto,
  type WhatsAppSetting,
  type WhatsAppTemplateStatus,
} from '@ecom/contracts';
import { Page, SaveBar, Section, Table, TableScroll, Td, Th } from '@/components/page';
import { Alert, Button, ButtonLink, Card, CopyButton, Field, Input, Pill, Select, Skeleton, Switch, type PillTone } from '@/components/ui';
import type { MessageKey } from '@/i18n/messages';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useSettings } from '@/pages/settings/schema';
import { settingValue } from '@/stores/settings.store';

const SETTING_KEY = 'whatsapp.config';

const STATUS_TONES: Record<WhatsAppTemplateStatus, PillTone> = {
  NOT_SUBMITTED: 'neutral',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  PAUSED: 'warning',
  DISABLED: 'danger',
};

const QUALITY_TONES: Record<string, PillTone> = { GREEN: 'success', YELLOW: 'warning', RED: 'danger' };

/** The setting as the form holds it: every update present, whatever an older store saved. */
function settingOf(value: unknown): WhatsAppSetting {
  const stored = (value ?? {}) as Partial<WhatsAppSetting>;
  return {
    events: { ...WHATSAPP_DEFAULTS.events, ...stored.events },
    language: stored.language === 'en' ? 'en' : 'ar',
    cartReminders: stored.cartReminders === true,
    inbox: stored.inbox !== false,
  };
}

/**
 * Settings → WhatsApp (ADR-105): the number as Meta sees it, where Meta's
 * receipts go, which order updates are sent and in which language, the
 * platform's templates with Meta's verdict on each, and a test message.
 * The keys themselves live in Integrations, encrypted, like every credential.
 */
export function WhatsAppPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { entries, saving, save } = useSettings();
  const stored = useMemo(() => settingOf(settingValue(entries, SETTING_KEY)), [entries]);
  const [draft, setDraft] = useState(stored);
  const [overview, setOverview] = useState<WhatsAppOverviewDto | null>(null);
  const [busy, setBusy] = useState<'submit' | 'refresh' | 'test' | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');

  useEffect(() => setDraft(stored), [stored]);
  useEffect(() => {
    withSession(() => adminApi.whatsapp.overview())
      .then(setOverview)
      .catch((caught: unknown) => setFailure(errorMessage(caught, 'Could not load WhatsApp.')));
  }, []);

  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);

  async function templates(action: 'submit' | 'refresh') {
    setBusy(action);
    setFailure(null);
    try {
      const page = await withSession(() => (action === 'submit' ? adminApi.whatsapp.submitTemplates() : adminApi.whatsapp.refreshTemplates()));
      setOverview((current) => (current ? { ...current, templates: page.items } : current));
    } catch (caught) {
      setFailure(errorMessage(caught, 'Meta did not take the request.'));
    } finally {
      setBusy(null);
    }
  }

  async function test() {
    setBusy('test');
    setFailure(null);
    setNotice(null);
    try {
      const result = await withSession(() => adminApi.whatsapp.test({ to: testTo }));
      if (result.sent) setNotice(t('whatsappTestSent'));
      else setFailure(t('whatsappTestFailed', { error: result.error ?? '' }));
    } catch (caught) {
      setFailure(errorMessage(caught, 'Could not send the test.'));
    } finally {
      setBusy(null);
    }
  }

  if (!overview) {
    return (
      <Page title={t('whatsapp')} subtitle={t('whatsappSubtitle')}>
        {failure ? <Alert>{failure}</Alert> : <Skeleton className="h-96" />}
      </Page>
    );
  }

  const verdict = (status: WhatsAppTemplateStatus, reason: string | null) => (
    <span className="flex flex-col items-start gap-1">
      <Pill tone={STATUS_TONES[status]}>{t(`whatsappStatus_${status}` as MessageKey)}</Pill>
      {reason ? <span className="max-w-48 text-xs text-ink-muted">{reason}</span> : null}
    </span>
  );

  return (
    <Page title={t('whatsapp')} subtitle={t('whatsappSubtitle')}>
      <div className="flex flex-col gap-4">
        {failure ? <Alert>{failure}</Alert> : null}
        {notice ? <Alert tone="success">{notice}</Alert> : null}

        {/* The connection, as Meta reports it. */}
        {!overview.enabled ? (
          <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-ink">{t('whatsappNotConnected')}</h2>
              <p className="mt-1 max-w-2xl text-sm text-ink-muted">{t('whatsappConnectHint')}</p>
            </div>
            <ButtonLink to="/settings/integrations" variant="secondary">
              {t('whatsappOpenIntegrations')}
            </ButtonLink>
          </Card>
        ) : (
          <Card className="grid gap-4 p-5 sm:grid-cols-3">
            <div>
              <p className="text-xs text-ink-muted">{t('whatsappNumber')}</p>
              <p dir="ltr" className="mt-1 text-start font-semibold text-ink">
                {overview.phone?.displayNumber || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-ink-muted">{t('whatsappVerifiedName')}</p>
              <p className="mt-1 font-semibold text-ink">{overview.phone?.verifiedName || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-ink-muted">{t('whatsappQuality')}</p>
              <p className="mt-1">
                {overview.phone?.qualityRating ? (
                  <Pill tone={QUALITY_TONES[overview.phone.qualityRating] ?? 'neutral'}>{overview.phone.qualityRating}</Pill>
                ) : (
                  '—'
                )}
              </p>
            </div>
            {overview.phoneError ? (
              <div className="sm:col-span-3">
                <Alert>{t('whatsappMetaError', { error: overview.phoneError })}</Alert>
              </div>
            ) : null}
          </Card>
        )}

        {/* Which updates go, and in which language. */}
        <Section title={t('whatsappUpdates')} subtitle={t('whatsappUpdatesHint')}>
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {WHATSAPP_EVENTS.map((event) => (
                <label key={event} className="flex items-center justify-between gap-3 rounded-control border border-line px-4 py-3">
                  <span className="text-sm text-ink">{t(`whatsappEvent_${event}` as MessageKey)}</span>
                  <Switch
                    checked={draft.events[event]}
                    label={t(`whatsappEvent_${event}` as MessageKey)}
                    onChange={(next) => setDraft((current) => ({ ...current, events: { ...current.events, [event]: next } }))}
                  />
                </label>
              ))}
            </div>
            {/* Customers' replies, into the support queue (ADR-107). */}
            <label className="flex items-start justify-between gap-3 rounded-control border border-line px-4 py-3">
              <span className="min-w-0">
                <span className="block text-sm text-ink">{t('whatsappInbox')}</span>
                <span className="mt-0.5 block text-xs text-ink-muted">{t('whatsappInboxHint')}</span>
              </span>
              <Switch checked={draft.inbox} label={t('whatsappInbox')} onChange={(next) => setDraft((current) => ({ ...current, inbox: next }))} />
            </label>
            {/* Marketing, so asked separately and off until turned on (ADR-106). */}
            <label className="flex items-start justify-between gap-3 rounded-control border border-line px-4 py-3">
              <span className="min-w-0">
                <span className="block text-sm text-ink">{t('whatsappCartReminders')}</span>
                <span className="mt-0.5 block text-xs text-ink-muted">{t('whatsappCartRemindersHint')}</span>
              </span>
              <Switch
                checked={draft.cartReminders}
                label={t('whatsappCartReminders')}
                onChange={(next) => setDraft((current) => ({ ...current, cartReminders: next }))}
              />
            </label>
            <Field label={t('whatsappLanguage')} htmlFor="whatsapp-language">
              <Select
                id="whatsapp-language"
                value={draft.language}
                onChange={(event) => setDraft((current) => ({ ...current, language: event.target.value as WhatsAppLanguage }))}
              >
                <option value="ar">{t('arabic')}</option>
                <option value="en">{t('english')}</option>
              </Select>
            </Field>
          </div>
        </Section>

        {/* The platform's templates, and Meta's verdict on each. */}
        <Section
          title={t('whatsappTemplates')}
          subtitle={t('whatsappTemplatesHint')}
          flush
          action={
            overview.enabled ? (
              <span className="flex flex-wrap gap-2">
                <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => void templates('refresh')}>
                  {busy === 'refresh' ? t('loading') : t('whatsappRefresh')}
                </Button>
                <Button size="sm" disabled={busy !== null} onClick={() => void templates('submit')}>
                  {busy === 'submit' ? t('loading') : t('whatsappSubmit')}
                </Button>
              </span>
            ) : undefined
          }
        >
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('whatsappUpdates')}</Th>
                  <Th>{t('arabic')}</Th>
                  <Th>{t('english')}</Th>
                </tr>
              </thead>
              <tbody>
                {overview.templates.map((template) => (
                  <tr key={template.name} className="border-t border-line align-top">
                    <Td className="max-w-xl">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ink">{t(`whatsappEvent_${template.event}` as MessageKey)}</span>
                        <Pill tone={template.category === 'MARKETING' ? 'info' : 'neutral'}>{t(`whatsappCategory_${template.category}` as MessageKey)}</Pill>
                      </span>
                      <span dir={locale === 'ar' ? 'rtl' : 'ltr'} className="mt-1 block text-xs text-ink-muted">
                        {template.body[locale]}
                      </span>
                      <span dir="ltr" className="mt-1 block font-mono text-[11px] text-ink-faint">
                        {template.name}
                      </span>
                    </Td>
                    <Td>{verdict(template.status.ar.status, template.status.ar.reason)}</Td>
                    <Td>{verdict(template.status.en.status, template.status.en.reason)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </Section>

        {/* Where Meta's receipts go. */}
        <Section title={t('whatsappWebhook')} subtitle={t('whatsappWebhookHint')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('whatsappWebhookUrl')} htmlFor="whatsapp-webhook" hint={overview.webhookUrl ? undefined : t('whatsappNoApiUrl')}>
              <div className="flex gap-2">
                <Input id="whatsapp-webhook" dir="ltr" readOnly value={overview.webhookUrl ?? ''} />
                {overview.webhookUrl ? <CopyButton value={overview.webhookUrl} label={t('copy')} copiedLabel={t('copied')} /> : null}
              </div>
            </Field>
            <Field label={t('whatsappVerifyToken')} htmlFor="whatsapp-token">
              <div className="flex gap-2">
                <Input id="whatsapp-token" dir="ltr" readOnly value={overview.verifyToken} />
                <CopyButton value={overview.verifyToken} label={t('copy')} copiedLabel={t('copied')} />
              </div>
            </Field>
          </div>
        </Section>

        {/* Proof that the keys and the number work. */}
        {overview.enabled ? (
          <Section title={t('whatsappTest')} subtitle={t('whatsappTestHint')}>
            <div className="flex flex-wrap items-end gap-3">
              <Field label={t('mobile')} htmlFor="whatsapp-test">
                <Input id="whatsapp-test" dir="ltr" inputMode="tel" placeholder="05XXXXXXXX" value={testTo} onChange={(event) => setTestTo(event.target.value)} />
              </Field>
              <Button variant="secondary" disabled={busy !== null || testTo.trim().length < 9} onClick={() => void test()}>
                {busy === 'test' ? t('loading') : t('whatsappTestSend')}
              </Button>
              <Link to="/settings/messages" className="text-sm font-medium text-brand-700 hover:underline">
                {t('messageLog')}
              </Link>
            </div>
          </Section>
        ) : null}
      </div>

      <SaveBar
        saving={saving === SETTING_KEY}
        dirty={dirty}
        onSave={() => void save(SETTING_KEY, draft)}
        onDiscard={() => setDraft(stored)}
      />
    </Page>
  );
}
